/**
 * Utilitário para evaliar expressões de visibilidade condicional
 * Suporta expressões simples como: {fieldName} > 5, {fieldName} === 'value', etc.
 */

/**
 * Avalia uma expressão de visibilidade baseada nos valores do formulário
 * @param condition - A condição a avaliar (ex: "{recomendation} < 5")
 * @param formValues - Objeto com os valores atuais do formulário
 * @returns boolean indicando se o elemento deve ser visível
 *
 * @example
 * evaluateVisibility("{rating} < 5", { rating: 3 }) // true
 * evaluateVisibility("{rating} >= 5", { rating: 3 }) // false
 * evaluateVisibility("{name} === 'John'", { name: 'John' }) // true
 */
export function evaluateVisibility(
  condition: string,
  formValues: Record<string, any>
): boolean {
  if (!condition || typeof condition !== 'string') {
    return true;
  }

  try {
    let expression = condition;

    const fieldRegex = /\{([a-zA-Z_$][a-zA-Z0-9_$]*)\}/g;
    const matches = expression.matchAll(fieldRegex);

    for (const match of matches) {
      const fieldName = match[1];
      const value = formValues[fieldName];

      const escapedValue =
        typeof value === 'string' ? `"${value.replace(/"/g, '\\"')}"` : value;

      expression = expression.replace(`{${fieldName}}`, String(escapedValue));
    }

    // eslint-disable-next-line no-new-func
    const fn = new Function('return ' + expression);
    const result = fn();

    return Boolean(result);
  } catch (error) {
    console.error(`Erro ao avaliar visibilidade: ${condition}`, error);
    return true;
  }
}

export function evaluateEnabled(
  condition: string | undefined,
  formValues: Record<string, any>
): boolean {
  if (!condition) {
    return true;
  }
  return evaluateVisibility(condition, formValues);
}

export function evaluateReadOnly(
  condition: string | undefined,
  formValues: Record<string, any>
): boolean {
  if (!condition) {
    return false;
  }
  return evaluateVisibility(condition, formValues);
}

export function evaluateRequired(
  condition: string | undefined,
  formValues: Record<string, any>
): boolean {
  if (!condition) {
    return false;
  }
  return evaluateVisibility(condition, formValues);
}

const visibilityUtils = {
  evaluateVisibility,
  evaluateEnabled,
  evaluateReadOnly,
  evaluateRequired,
};

export default visibilityUtils;
